import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { FiChevronLeft } from 'react-icons/fi';
import Box from 'mineral-ui/Box';
import Flex from './Flex';
import RouterLink from '../RouterLink';
import { Button, Text } from '..';
import UnstyledLink from '../UnstyledLink';

/* Note: subtitle requires a title, otherwise it will not be displayed
 */
const propTypes = {
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      content: PropTypes.node.isRequired,
      href: PropTypes.string.isRequired,
    }),
  ),
  children: PropTypes.node.isRequired,
  fullWidth: PropTypes.bool,
  primaryAction: PropTypes.shape({
    content: PropTypes.node.isRequired,
    onClick: PropTypes.func,
    href: PropTypes.string,
  }),
  secondaryActions: PropTypes.arrayOf(
    PropTypes.shape({
      content: PropTypes.node.isRequired,
      onClick: PropTypes.func,
      href: PropTypes.string,
    }),
  ),
  title: PropTypes.string,
  singleColumn: PropTypes.bool,
  subtitle: PropTypes.string,
  notifier: PropTypes.shape({
    text: PropTypes.string,
    colorVariant: PropTypes.string,
  }),
  variableWidth: PropTypes.number,
};

const defaultProps = {
  breadcrumbs: undefined,
  fullWidth: false,
  variableWidth: undefined,
  primaryAction: undefined,
  secondaryActions: undefined,
  title: undefined,
  singleColumn: false,
  subtitle: undefined,
  notifier: undefined,
};

const PageContainer = styled.div`
  max-width: ${({
    fullWidth, singleColumn, variableWidth, theme,
  }) => {
    if (fullWidth) {
      return 'inherit';
    }
    if (singleColumn) {
      return `calc(${theme.fontSize_ui} * 45)`;
    }
    if (variableWidth) {
      return `calc(${theme.fontSize_ui} * ${variableWidth})`;
    }
    return `calc(${theme.fontSize_ui} * 90)`;
  }};
  margin: auto;
`;

export default function Page({
  breadcrumbs,
  fullWidth,
  children,
  primaryAction,
  secondaryActions,
  singleColumn,
  subtitle,
  title,
  notifier,
  variableWidth,
}) {
  let breadcrumbsMarkup;
  if (breadcrumbs) {
    breadcrumbsMarkup = breadcrumbs.map((breadcrumb, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <UnstyledLink key={index} href={breadcrumb.href}>
        <FiChevronLeft style={{ verticalAlign: 'text-bottom' }} size="1.4em" />
        {breadcrumb.content}
      </UnstyledLink>
    ));
  }

  let titleMarkup;
  let subtitleMarkup;
  if (title) {
    titleMarkup = <Text as="h1">{title}</Text>;
    if (subtitle) {
      subtitleMarkup = <Text as="h2">{subtitle}</Text>;
    }
  }

  let primaryActionMarkup;
  if (primaryAction) {
    if (primaryAction.href) {
      primaryActionMarkup = (
        <Button
          primary
          as={RouterLink}
          href={primaryAction.href}
          onClick={primaryAction.onClick}
        >
          {primaryAction.content}
        </Button>
      );
    } else {
      primaryActionMarkup = (
        <Button primary onClick={primaryAction.onClick}>
          {primaryAction.content}
        </Button>
      );
    }
  }

  let secondaryActionsMarkup;
  if (secondaryActions) {
    secondaryActionsMarkup = secondaryActions.map((action, index) => {
      if (action.href) {
        return (
          // eslint-disable-next-line react/no-array-index-key
          <RouterLink key={index} href={action.href}>
            <Button minimal size="medium" onClick={action.onClick}>
              {action.content}
            </Button>
          </RouterLink>
        );
      }

      return (
        // eslint-disable-next-line react/no-array-index-key
        <Button key={index} minimal size="medium" onClick={action.onClick}>
          {action.content}
        </Button>
      );
    });
    secondaryActionsMarkup = (
      <Box marginTop="-0.5em">
        {secondaryActionsMarkup}
      </Box>
    );
  }
  let messageNotifier;
  if (notifier) {
    messageNotifier = <Text color={notifier.colorVariant} as="h3">{notifier.text}</Text>;
  }

  return (
    <PageContainer fullWidth={fullWidth} singleColumn={singleColumn} variableWidth={variableWidth}>
      <Box marginStart="-0.25em">
        {breadcrumbsMarkup}
      </Box>
      <Box marginTop="0.5em">
        <Flex>
          <Flex.Item>
            {titleMarkup}
            {subtitleMarkup}
            {secondaryActionsMarkup}
          </Flex.Item>
          <Flex.Item marginStart="auto">
            {primaryActionMarkup}
          </Flex.Item>
        </Flex>
      </Box>
      <Box marginTop="1em">
        {messageNotifier}
        {children}
      </Box>
    </PageContainer>
  );
}

Page.propTypes = propTypes;
Page.defaultProps = defaultProps;
